"use client";

import { useState } from "react";
import { Button, Icon } from "~components/ui";
import type { PageContext } from "~lib/storyblok";
import { SectionBlockHeader } from "../section-block-header/section-block-header";
import type { SectionBlockHeaderSchema } from "../section-block-header/types";
import { SectionEventsItem } from "../section-events-item/section-events-item";
import type { SectionEventsItemSchema } from "../section-events-item/types";
import type { SectionEventsSchema } from "./types";

interface EventItemsProps extends PageContext {
  blok: SectionEventsSchema;
  events: SectionEventsItemSchema[];
  header: SectionBlockHeaderSchema | undefined;
}

const MAX_EVENTS = 6;
const STEP = 3;

function EventItems({
  events,
  blok,
  header,
  ...context
}: Readonly<EventItemsProps>) {
  const [visible, setVisible] = useState(Math.min(MAX_EVENTS, events.length));

  const hasEvents = !!events.length;
  const viewMoreLabel = blok.viewMore;
  const hasMoreEvents = visible < events.length;
  const onViewMore = () =>
    setVisible(prev => Math.min(prev + STEP, events.length));

  return hasEvents ? (
    <div className="flex flex-col gap-md">
      {header && <SectionBlockHeader blok={header} {...context} />}

      <div className="grid gap-md [grid-template-columns:repeat(auto-fit,minmax(16rem,1fr))] lg:grid-cols-3 sm:grid-cols-2">
        {events.map(event => (
          <SectionEventsItem
            key={event._uid}
            blok={blok}
            event={event}
            {...context}
          />
        ))}
      </div>

      {hasMoreEvents && (
        <Button
          className="mx-auto w-max"
          variant="link-plain"
          onClick={onViewMore}
        >
          <span>{viewMoreLabel}</span>
          <Icon name="ArrowDown" weight="bold" />
        </Button>
      )}
    </div>
  ) : null;
}

export { EventItems };
